import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AIInsights from "@/components/AIInsights";
import { StarRating } from "@/components/StarRating";
import api, { formatApiError } from "@/lib/api";
import ConservationPill from "@/components/ConservationPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Thermometer, Award, ShieldCheck, Package } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Share2 } from "lucide-react";
import { toast } from "sonner";

export default function LotDetail() {
  const { lotId } = useParams();
  const { fmt, fmtDual } = useCurrency();
  const [data, setData] = useState(null);
  const [qty, setQty] = useState(1);
  const [addr, setAddr] = useState("");
  const [placing, setPlacing] = useState(false);
  const [docs, setDocs] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/lots/${lotId}`).then(r => {
      setData(r.data);
      const ids = r.data.lot.health_cert_ids || [];
      if (ids.length) {
        Promise.all(ids.map(id => api.get(`/files/${id}/meta`).then(m => m.data).catch(() => null)))
          .then(ms => setDocs(ms.filter(Boolean)));
      }
    }).catch(() => setData(false));
  }, [lotId]);

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/files/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed — sign in required"); }
  };

  const placeOrder = async () => {
    if (!user || user === false) { nav("/login"); return; }
    setPlacing(true);
    try {
      const { data: order } = await api.post("/orders", { lot_id: lotId, quantity: qty, shipping_address: addr });
      await api.post(`/orders/${order.order_id}/capture`);
      toast.success("Escrow locked · funds held pending delivery");
      nav(`/orders/${order.order_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPlacing(false); }
  };

  if (data === null) return <div className="p-10 text-center text-stone-500">Loading…</div>;
  if (data === false) return <div className="p-10 text-center text-stone-500">Lot not found.</div>;

  const { lot, seller } = data;
  const subtotal = qty * (lot.price_per_unit || 0);
  const fee = Math.round(subtotal * 0.06 * 100) / 100;
  const payout = Math.round((subtotal - fee) * 100) / 100;
  const total = subtotal.toFixed(2);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-12 gap-8">
      <div className="lg:col-span-8">
        <div className="surface-card overflow-hidden">
          {lot.image_url && <img src={lot.image_url} alt={lot.title} className="w-full h-80 object-cover" />}
          <div className="p-6">
            <div className="flex items-center gap-2 flex-wrap">
              <ConservationPill status={lot.conservation_status} />
              <span className="text-xs mono uppercase px-2.5 py-1 border rounded-full pill-stable">{lot.species}</span>
              <span className="text-xs mono uppercase px-2.5 py-1 border rounded-full pill-stable">{lot.lot_type.replace("_", " ")}</span>
            </div>
            <h1 className="heading text-3xl font-bold mt-3">{lot.title}</h1>
            <div className="mono text-xs text-stone-500 mt-1">{lot.lot_id}</div>
            <p className="mt-4 text-stone-700">{lot.pedigree_notes}</p>

            <h3 className="heading text-lg font-semibold mt-6">Donor Animal</h3>
            <table className="data-table mt-2">
              <tbody>
                <tr><td className="w-1/3">Donor</td><td className="mono">{lot.donor_name}</td></tr>
                {lot.donor_registration && <tr><td>Registration</td><td className="mono">{lot.donor_registration}</td></tr>}
                {lot.donor_ear_tag && <tr><td>USDA Ear Tag</td><td className="mono">{lot.donor_ear_tag}</td></tr>}
                <tr><td>Breed</td><td>{lot.breed}</td></tr>
                {lot.collection_date && <tr><td>Collection date</td><td className="mono">{lot.collection_date}</td></tr>}
                {lot.lab_accreditation && <tr><td>Lab accreditation</td><td className="mono">{lot.lab_accreditation}</td></tr>}
                <tr><td>Cryogenic storage</td><td className="mono">{lot.cryogenic_temp_c}°C (LN₂)</td></tr>
              </tbody>
            </table>

            {docs.length > 0 && (
              <>
                <h3 className="heading text-lg font-semibold mt-8">Health &amp; Compliance Documents</h3>
                <ul data-testid="lot-documents" className="mt-2 space-y-2">
                  {docs.map((d) => (
                    <li key={d.id} className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2 bg-white">
                      <span className="text-sm mono">{d.original_filename} <span className="text-stone-400">({(d.size / 1024).toFixed(0)} KB)</span></span>
                      <Button data-testid={`lot-doc-download-${d.id}`} size="sm" variant="outline" onClick={() => downloadDoc(d)}>Download</Button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <h3 className="heading text-lg font-semibold mt-8">Genetic Attributes</h3>
            <div className="mt-2 grid sm:grid-cols-2 gap-2">
              {Object.entries(lot.genetic_attributes || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2 bg-white">
                  <span className="text-sm text-stone-600">{k}</span>
                  <span className="mono font-semibold text-stone-900">{v}</span>
                </div>
              ))}
              {(!lot.genetic_attributes || Object.keys(lot.genetic_attributes).length === 0) && (
                <div className="text-sm text-stone-500">No genetic attributes recorded.</div>
              )}
            </div>

            <AIInsights lotId={lotId} />
          </div>
        </div>
      </div>

      <aside className="lg:col-span-4 space-y-4">
        <div className="surface-card p-5">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Price per unit</div>
          <div className="heading text-3xl font-extrabold mono">{fmtDual(lot.price_per_unit)}</div>
          <div className="text-sm text-stone-500 mt-1">{lot.quantity} available</div>
          <Button data-testid="lot-share-facebook" variant="outline" size="sm" className="mt-3 w-full"
                  onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, "_blank", "width=600,height=540")}>
            <Share2 className="h-3.5 w-3.5 mr-1" /> Share on Facebook
          </Button>

          <div className="mt-4 space-y-3">
            <div>
              <Label>Quantity</Label>
              <Input data-testid="lot-qty-input" type="number" min={1} max={lot.quantity} value={qty} onChange={(e) => setQty(Math.max(1, Math.min(lot.quantity, parseInt(e.target.value) || 1)))} />
            </div>
            <div>
              <Label>Ship-to address</Label>
              <Textarea data-testid="lot-address-input" value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Ranch, city, state, country" />
            </div>
            <div className="pt-2 border-t space-y-1.5" data-testid="fee-breakdown">
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Sale total (held in escrow)</span>
                <span className="mono font-semibold" data-testid="lot-total">{fmtDual(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Platform fee (6%)</span>
                <span className="mono text-stone-700" data-testid="lot-fee">−{fmt(fee)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-stone-500">Seller payout on release</span>
                <span className="mono text-emerald-700" data-testid="lot-payout">{fmt(payout)}</span>
              </div>
              <p className="text-[10px] text-stone-400">Fees shown openly to buyers and sellers per the <Link to="/terms" className="underline">Terms &amp; Conditions</Link>.</p>
              <p className="text-[10px] text-stone-400">International shipping may incur additional taxes &amp; customs/import fees — these are not the responsibility of GeneticsTrade. Check your local area for fees you may incur.</p>
            </div>

            <Dialog>
              <DialogTrigger asChild>
                <Button data-testid="lot-buy-btn" disabled={!addr || placing} className="btn-earth w-full h-11">
                  <Package className="h-4 w-4 mr-1" /> Reserve & lock escrow
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Confirm escrow order</DialogTitle></DialogHeader>
                <div className="text-sm text-stone-600 space-y-2">
                  <p>You are about to place <strong>ORD (new)</strong> with <strong>${total}</strong> held in escrow.</p>
                  <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2">
                    Funds are <strong>held in escrow</strong> at checkout and <strong>released only after you confirm compliant delivery</strong>.
                  </p>
                </div>
                <DialogFooter>
                  <Button data-testid="lot-confirm-order-btn" className="btn-forest" onClick={placeOrder} disabled={placing}>{placing ? "Placing…" : "Confirm & lock funds"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {seller && (
          <div className="surface-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-stone-400">Sold by</div>
            <div className="heading text-lg font-semibold">{seller.name}</div>
            <div className="text-sm text-stone-500">{seller.organization}</div>
            <div className="mt-3 flex items-center gap-2 text-sm">
              <ShieldCheck className={`h-4 w-4 ${seller.accreditation_status === "verified" ? "text-emerald-600" : "text-stone-400"}`} />
              <span className="mono uppercase text-xs">{seller.accreditation_status}</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm">
              <StarRating value={seller.rating_avg || 0} size="h-4 w-4" testId="seller-rating" />
              <span className="mono">{seller.rating_avg?.toFixed(1) || "—"}</span>
              <span className="text-stone-400">({seller.rating_count || 0})</span>
              {seller.rating_avg >= 4.5 && seller.rating_count >= 5 && <span data-testid="seller-top-rated-badge" className="text-[10px] mono uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Top rated</span>}
            </div>
          </div>
        )}

        <div className="surface-card p-5 text-sm text-stone-600">
          <div className="flex items-center gap-2 text-emerald-700"><Thermometer className="h-4 w-4" /> <span className="mono">−196°C LN₂ cold-chain</span></div>
          <div className="flex items-center gap-2 mt-2 text-emerald-700"><Award className="h-4 w-4" /> Health certs verified by admin</div>
        </div>
      </aside>
    </div>
  );
}
